import type { ContactRequestStatus, SellingStatus, ShipmentStatus, ShopStatus } from "@platform/contracts";
import type {
  afterSaleCase,
  enterpriseVerification,
  fulfilment,
  inquiry,
  instruction,
  order,
  paymentIntent,
  productLifecycle,
  quoteVersion,
  revisionLifecycle,
  scheduleItem,
  sellerEnablement,
} from "@platform/domain";

/** Meridian's five status tones (docs/design/meridian.md, Status tones). */
export const TONES = ["neutral", "progress", "attention", "done", "problem"] as const;
export type Tone = (typeof TONES)[number];
export type Side = "buyer" | "seller";

/** A state whose tone follows who must act: that side sees attention, the other progress. */
type Toned = Tone | { readonly buyer: Tone; readonly seller: Tone };
const buyerActs = { buyer: "attention", seller: "progress" } as const;
const sellerActs = { buyer: "progress", seller: "attention" } as const;

type StateOf<M extends { readonly states: readonly string[] }> = M["states"][number];
type Table<S extends string> = { readonly [state in S]: Toned };

/**
 * Every state the product shows, by kind. The domain's machines are typed from their states, so a new state without
 * a tone fails the typecheck, and the test walks each machine.
 */
const TABLE = {
  order: {
    awaiting_payment: buyerActs,
    in_progress: sellerActs,
    awaiting_confirmation: buyerActs,
    completed: "done",
    cancelled: "neutral",
  } satisfies Table<StateOf<typeof order>>,
  fulfilment: {
    not_shipped: "neutral",
    partially_shipped: "progress",
    shipped: "progress",
    delivered: "done",
  } satisfies Table<StateOf<typeof fulfilment>>,
  scheduleItem: {
    not_due: "neutral",
    due: buyerActs,
    paid: "done",
    released: "done",
    partially_refunded: "neutral",
    refunded: "neutral",
    cancelled: "neutral",
  } satisfies Table<StateOf<typeof scheduleItem>>,
  inquiry: {
    open: sellerActs,
    quoted: buyerActs,
    countered: sellerActs,
    accepted: "done",
    declined: "problem",
    withdrawn: "neutral",
    expired: "neutral",
  } satisfies Table<StateOf<typeof inquiry>>,
  quote: {
    issued: buyerActs,
    countered: sellerActs,
    accepted: "done",
    superseded: "neutral",
    expired: "neutral",
  } satisfies Table<StateOf<typeof quoteVersion>>,
  case: {
    requested: sellerActs,
    seller_responding: sellerActs,
    agreed: sellerActs,
    executing: sellerActs,
    escalated: "problem",
    under_review: "problem",
    resolved: "done",
    closed: "neutral",
    withdrawn: "neutral",
  } satisfies Table<StateOf<typeof afterSaleCase>>,
  verification: {
    submitted: "progress",
    under_review: "progress",
    verified: "done",
    rejected: "problem",
    suspended: "problem",
    closed: "neutral",
  } satisfies Table<StateOf<typeof enterpriseVerification>>,
  sellerEnablement: {
    no_shop: "neutral",
    shop_opened: "neutral",
    kyb_pending: "progress",
    can_sell: "done",
    kyb_rejected: "problem",
  } satisfies Table<StateOf<typeof sellerEnablement>>,
  product: {
    draft: "neutral",
    pending_review: "progress",
    live: "done",
    off_shelf: "neutral",
    blocked: "problem",
  } satisfies Table<StateOf<typeof productLifecycle>>,
  revision: { pending: "progress", applied: "done", rejected: "problem" } satisfies Table<
    StateOf<typeof revisionLifecycle>
  >,
  paymentIntent: {
    requires_payment: buyerActs,
    processing: "progress",
    held: "progress",
    released: "done",
    partially_refunded: "neutral",
    refunded: "neutral",
    failed: "problem",
    cancelled: "neutral",
  } satisfies Table<StateOf<typeof paymentIntent>>,
  instruction: {
    pending: "progress",
    sent: "progress",
    confirmed: "done",
    failed: "problem",
    cancelled: "neutral",
  } satisfies Table<StateOf<typeof instruction>>,
  shipment: { in_transit: "progress", delivered: "done", exception: "problem" } satisfies Table<
    (typeof ShipmentStatus.options)[number]
  >,
  /** A change order waits on the party that did not propose it, which is not a side, so it shows progress. */
  changeOrder: {
    proposed: "progress",
    accepted: "done",
    declined: "problem",
    withdrawn: "neutral",
  } satisfies Table<"proposed" | "accepted" | "declined" | "withdrawn">,
  /** A bank transfer the buyer reported, until the seller confirms the money arrived. */
  transfer: { submitted: sellerActs, confirmed: "done", rejected: "problem" } satisfies Table<
    "submitted" | "confirmed" | "rejected"
  >,
  shop: { draft: "neutral", active: "done", suspended: "problem", closed: "neutral" } satisfies Table<
    (typeof ShopStatus.options)[number]
  >,
  selling: { not_ready: "neutral", can_sell: "done", suspended: "problem" } satisfies Table<
    (typeof SellingStatus.options)[number]
  >,
  /** A person's place in an enterprise, and an invitation to it (card 113). */
  membership: { invited: "progress", active: "done", left: "neutral" } satisfies Table<
    "invited" | "active" | "left"
  >,
  invitation: { pending: "progress", accepted: "done", expired: "neutral" } satisfies Table<
    "pending" | "accepted" | "expired"
  >,
  /** An external contact request: the person it was sent to answers it (card 113). */
  contactRequest: {
    pending: "progress",
    accepted: "done",
    declined: "neutral",
    expired: "neutral",
  } satisfies Table<(typeof ContactRequestStatus.options)[number]>,
} as const;

export type StatusKind = keyof typeof TABLE;
export type StatusOf<K extends StatusKind> = keyof (typeof TABLE)[K] & string;

/**
 * The tone of a state (docs/design/meridian.md: "One status, two sides"). Where the tone follows who must act, `side`
 * is the reader's; without one (an operator, say) the state shows progress.
 */
export function statusTone<K extends StatusKind>(kind: K, state: StatusOf<K>, side?: Side): Tone {
  const toned = (TABLE[kind] as Readonly<Record<string, Toned>>)[state];
  if (toned === undefined) return "neutral";
  if (typeof toned === "string") return toned;
  return side === undefined ? "progress" : toned[side];
}

/**
 * An after-sales case's tone (card 112): its status says whether it is open and how it ended; while it is open, who
 * must act is `waitingOn`, so the side it waits on sees attention and the other progress.
 */
export function caseTone(status: StatusOf<"case">, waitingOn: Side | "platform" | null, side?: Side): Tone {
  const tone = statusTone("case", status, side);
  if (tone === "done" || tone === "neutral" || tone === "problem") return tone;
  if (waitingOn === null || side === undefined) return "progress";
  return waitingOn === side ? "attention" : "progress";
}
